import React, { useCallback, useMemo, useRef, useState } from 'react'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import MaldivesDirectSearchFlightCalendar from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/useFlightCalendarData'
import { FlightsClassTypes } from 'constants/flight'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { parseAsBoolean, useQueryState } from 'nuqs'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import CharterBundleCalendarActions from './CharterBundleCalendarActions'
import Group from 'components/utils/Group'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  onFareClassChange?: (fareClass: FlightsClassTypes) => void
  offer: App.HotelOffer
  duration: number
  rooms: Array<App.Occupants>
  checkInDate?: string
  airport?: App.Airport
  multipleRoomBookingActive: boolean
  charterBundleRoute: App.CharterBundleRoute
}

/**
 * Charter Bundle Calendar Modal - Responsive
 * Renders desktop calendar on large screens, mobile calendar on small screens
 * Uses ModalContext pattern - resolves with selected dates when user selects
 */
function CharterBundleCalendarModal(props: Props) {
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    multipleRoomBookingActive,
    charterBundleRoute,
    selectedMonth,
    onMonthChange,
    fareClass,
    priceMode,
    onDurationChange,
    durationOptions,
    onFareClassChange,
  } = props

  const modalElement = useModalElementContext<{ departDate: string, returnDate: string } | undefined>()
  const modalBodyRef = useRef<HTMLDivElement | null>(null)
  const isMobileScreen = useScreenSizeOnly('mobile')

  const [localSelectedMonth, setLocalSelectedMonth] = useState(selectedMonth)

  // Read luxPlus state from URL (same as parent component)
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(true)
  )

  // Get dateRangesByMonth from charter flight journeys API
  // Use fixed date range to fetch all available flights, then extract all dates from the response
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass,
    disabled: rooms.length === 0,
  })

  // Filter dateRangesByMonth by selected nights
  // Note: We filter by durationDays instead of nights because durationDays represents the total trip duration
  // which matches the user's selection (5 nights = ~7 days, 12 nights = ~14 days)
  const dateRangesByMonth = useMemo(() => {
    // Map selected nights to expected durationDays
    // 5 nights bundle = ~7 days total trip (departure to return)
    // 12 nights bundle = ~14 days total trip (departure to return)
    const expectedDurationDays = duration === 5 ? 7 : 14

    const filtered: Record<string, Array<{ departureDate: string; returnDate: string; checkIn: string; checkOut: string; durationDays: number; nights: number }>> = {}

    Object.entries(allDateRangesByMonth).forEach(([month, ranges]) => {
      // Filter by durationDays which represents the total trip duration
      const filteredRanges = ranges.filter(range => range.durationDays === expectedDurationDays)
      if (filteredRanges.length > 0) {
        filtered[month] = filteredRanges
      }
    })

    return filtered
  }, [allDateRangesByMonth, duration])

  // Fetch calendar data based on current context values
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass,
    dateRangesByMonth,
    multipleRoomBookingActive,
    charterBundleRoute,
  })

  // Calculate selected option ID based on current checkIn date and duration
  const selectedOptionId = useMemo(() => {
    if (!checkInDate) return undefined
    return `${checkInDate}-${duration}n`
  }, [checkInDate, duration])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => {
    // Return the selected dates via modal's resolve mechanism
    // Return both outer dates (for display) and inner dates (for hotel queries)
    modalElement.resolve(option)
  }, [modalElement])

  const handleLocalMonthChange = useCallback((month: string) => {
    // Update local state for immediate UI feedback
    setLocalSelectedMonth(month)
    // Also update parent state for consistency
    onMonthChange(month)
  }, [onMonthChange])

  const calendarProps = {
    onDateSelect: handleDateSelect,
    nights: duration,
    occupancies: rooms,
    luxPlusEnabled,
    itemBundleDataByDate: flightCalendarData.itemBundleDataByDate,
    dateRangesByMonth,
    selectedMonth: localSelectedMonth,
    onMonthChange: handleLocalMonthChange,
    fareClass,
    priceMode,
    selectedOptionId,
    mode: 'offer' as const,
  }

  const actionsElement = (
    <CharterBundleCalendarActions
      nights={duration}
      onDurationChange={onDurationChange}
      durationOptions={durationOptions}
      luxPlusEnabled={luxPlusEnabled}
      onLuxPlusToggle={setLuxPlusEnabled}
      fareClass={fareClass}
      onFareClassChange={onFareClassChange}
    />
  )

  const mobileTitle = 'Select your trip dates'
  const desktopTitle = (
    <Group direction="horizontal" horizontalAlign="space-between" gap={16} verticalAlign="center" fullWidth>
      <span>Select your trip dates</span>
      {actionsElement}
    </Group>
  )
  const modalTitle = isMobileScreen ? mobileTitle : desktopTitle

  return (
    <Modal
      ref={modalBodyRef}
      title={modalTitle}
      size="XL"
      isPrimaryActionHidden
      onClose={() => modalElement.resolve(undefined)}
    >
      {isMobileScreen && (
        <MaldivesDirectSearchFlightCalendarMobile
          {...calendarProps}
          headerActions={actionsElement}
          scrollContainerRef={modalBodyRef}
        />
      )}
      {!isMobileScreen && (
        <MaldivesDirectSearchFlightCalendar {...calendarProps} />
      )}
    </Modal>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer! as App.HotelOffer,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  airport: state.airport,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  charterBundleRoute: state.charterBundleRoute!,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
}), CharterBundleCalendarModal)
